import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading, ErrorState } from "../components/States";
import ListingPropertyAccess from "../components/ListingPropertyAccess";
import { ListingForm } from "./HostNewListing";
import { api } from "../lib/api";
import { setPageTitle } from "../lib/format";

export default function HostEditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initial, setInitial] = useState(null);
  const [spot, setSpot] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPageTitle("Edit Listing");
    api.getSpot(id).then((s) => { setSpot(s); setInitial({
      title: s.title, description: s.description, address: s.address,
      latitude: s.latitude, longitude: s.longitude,
      price_per_hour: String(s.price_per_hour ?? ""),
      price_6h: s.price_6h != null ? String(s.price_6h) : "",
      price_12h: s.price_12h != null ? String(s.price_12h) : "",
      price_24h: s.price_24h != null ? String(s.price_24h) : "",
      monthly_enabled: !!s.monthly_enabled,
      monthly_price: s.monthly_price != null ? String(s.monthly_price) : "",
      space_type: s.space_type, amenities: s.amenities || [],
      booking_policy: s.booking_policy || "open",
      cancellation_policy: s.cancellation_policy || "",
      max_duration_hours: s.max_duration_hours != null ? String(s.max_duration_hours) : "",
      max_vehicle_height_m: s.max_vehicle_height_m != null ? String(s.max_vehicle_height_m) : "",
      vehicle_restrictions: s.vehicle_restrictions || "",
      access_instructions: "", // private field; host re-enters to change it
      images: (s.images || []).join("\n"),
      country: s.country || "", region: s.region || "", postal_code: s.postal_code || "",
      timezone: s.timezone || "",
      flat_rate_enabled: !!s.flat_rate_enabled,
      flat_rate_duration_choice: s.flat_rate_duration_hours === 12 || s.flat_rate_duration_hours === 24 ? String(s.flat_rate_duration_hours) : "custom",
      flat_rate_custom_hours: s.flat_rate_duration_hours && ![12, 24].includes(s.flat_rate_duration_hours) ? String(s.flat_rate_duration_hours) : "",
      flat_rate_price: s.flat_rate_price_cents != null ? String(s.flat_rate_price_cents / 100) : "",
      available_days: s.available_days || [],
      available_start_hour: s.available_start_hour != null ? String(s.available_start_hour) : "",
      available_end_hour: s.available_end_hour != null ? String(s.available_end_hour) : "",
      authorization_type: s.authorization_type || "",
      location_confirmed: !!s.location_confirmed,
      attestation: s.attestation || null,
    }); }).catch((e) => setError(e.message));
  }, [id]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="edit-listing-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Host</p>
        <h1 className="type-display mt-3">Edit listing</h1>
        <p className="mt-1 text-sm text-zinc-400">Changes apply immediately to new bookings.</p>
        {spot?.property_code && <div className="mt-8"><ListingPropertyAccess spot={spot} /></div>}
        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={() => window.location.reload()} /> : !initial ? <PageLoading label="Loading listing…" /> : (
            <ListingForm initial={initial} submitLabel="Save Changes" testPrefix="edit-listing"
              onSubmit={async (body) => {
                try {
                  const payload = { ...body };
                  if (!payload.access_instructions) delete payload.access_instructions; // keep existing
                  // Unchanged location must not requeue the listing for review.
                  if (initial && payload.address === initial.address && payload.latitude === initial.latitude && payload.longitude === initial.longitude) {
                    delete payload.address; delete payload.latitude; delete payload.longitude;
                  }
                  await api.updateSpot(id, payload);
                  toast.success("Listing updated");
                  navigate("/host");
                } catch (e) { toast.error(e.message); }
              }} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
